import { useParams, Link } from 'react-router-dom';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleById, getRecentArticles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(Number(id));

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = getRecentArticles(4).filter((a) => a.id !== article.id).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.cover,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '美女直播平台精选',
    },
  };

  return (
    <>
      <PageMeta
        title={`${article.title} - 美女直播平台精选`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://live-platform.example.com/articles/${article.id}`}
        ogImage={article.cover}
        ogType="article"
        schemaData={[articleSchema]}
      />

      <article className="pt-20 pb-16 md:pb-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '直播资讯', path: '/articles' },
                { label: article.title },
              ]}
            />
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {article.category}
              </span>
              <span className="text-foreground-400 text-xs">{article.readTime}</span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 text-foreground-500 text-sm mb-6">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views.toLocaleString()} 阅读
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 mb-8">
              {article.tags.map((tag: string) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src={article.cover}
              alt={article.title}
              className="w-full aspect-[16/9] object-cover"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            {article.content.split('\n\n').map((paragraph: string, idx: number) => (
              <p key={idx} className="text-foreground-700 text-base leading-relaxed mb-5">
                {paragraph.trim()}
              </p>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <h3 className="font-heading font-semibold text-foreground-950 text-xl mb-5">
              相关文章
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/articles/${related.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={related.cover}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="font-heading font-medium text-foreground-950 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {related.title}
                    </h4>
                    <p className="text-foreground-400 text-xs mt-2">{related.date} · {related.readTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-primary-200 text-primary-600 hover:bg-primary-50 font-medium text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回文章列表
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}